// How long a deleted file stays in the trash, in the API's words (`TrashSummary.days`, backend
// `assets/contracts.py` `TRASH_DAYS`: 7 since Daniil's call of 2026-10-22, was 30), so a Delete's
// question never names another number than the restore and the sweep keep.
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { getGetTrashQueryOptions, useGetTrash } from "../../api/generated/endpoints/assets/assets";
import { countOf } from "../../ui/format";

// The days change only with a release, so a read stays good for the session's hour: a question
// opened after the prefetch below shows them at once, with no "in the trash" first.
const DAYS_FRESH_MS = 60 * 60 * 1000;

/**
 * "in the trash for 7 days", or "in the trash" while the brand's trash is read. Read only while
 * `enabled` (a Delete's question open), so a page that only might ask sends nothing; its days are
 * there already where the Delete's way in prefetched them (`usePrefetchTrashDays`).
 */
export function useTrashStay(teamId: string, brandId: string, enabled = true): string {
  const days = useGetTrash(teamId, brandId, { query: { enabled, staleTime: DAYS_FRESH_MS } }).data?.days;
  return days === undefined ? "in the trash" : `in the trash for ${countOf(days, "day")}`;
}

/**
 * Reads the brand's trash days ahead of a Delete's question, once its way in shows (a card's ⋯
 * menu opening, a selection made, a tile's bin under the pointer): never on a page's load.
 */
export function usePrefetchTrashDays(teamId: string, brandId: string): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.prefetchQuery({ ...getGetTrashQueryOptions(teamId, brandId), staleTime: DAYS_FRESH_MS });
  }, [queryClient, teamId, brandId]);
}
